import * as React from 'react'
import { useSnapshot } from 'valtio'
import { dataState } from '../../../store/dataState.ts'
import { DEFAULT_ZOOM } from '../types.ts'
import { PetMarker } from './PetMarker.tsx'
import { controlState } from '../../../store/controlState.ts'
import { drawerState } from '../../../store/drawerState.ts'
import { mapState, setBounds, setZoom } from '../../../store/mapState.ts'
import { IDeviceStatus } from '../../../interfaces/types.ts'

export const LastKnownLocations = () => {
    const devices = (useSnapshot(dataState) as typeof dataState).devices
    const pets = (useSnapshot(dataState) as typeof dataState).pets
    const follow = useSnapshot(controlState).followPets
    const map = (useSnapshot(mapState) as typeof mapState).map

    // Track whether we've done the initial fitBounds so we don't fight user pan/zoom on every update.
    const hasFittedRef = React.useRef(false)

    // Memoize markers so we only rebuild when devices or pets actually change.
    const { markers, bounds } = React.useMemo(() => {
        const markers: React.ReactNode[] = []
        const bounds = new window.google.maps.LatLngBounds()
        let hasLocations = false

        devices?.forEach(d => {
            if (d.petId && d.lastLocation && Object.keys(d.lastLocation).length) {
                const pet = pets.find(p => d.petId === p._id)
                if (pet) {
                    const position = new window.google.maps.LatLng({
                        lat: d.lastLocation?.properties?.latitude,
                        lng: d.lastLocation?.properties?.longitude
                    })
                    markers.push(
                        <PetMarker
                            key={pet._id}
                            pet={pet}
                            position={position}
                            status={d.lastLocation.properties as unknown as IDeviceStatus}
                        />
                    )
                    bounds.extend(position)
                    hasLocations = true
                }
            }
        })

        return { markers: hasLocations ? markers : [], bounds: hasLocations ? bounds : null }
    }, [devices, pets])

    // Fit bounds once on initial load (when map + markers first become available).
    // Subsequent device updates just move markers without re-fitting.
    React.useEffect(() => {
        if (map && bounds && follow && !hasFittedRef.current) {
            hasFittedRef.current = true
            setBounds(bounds)
            const isMobile = controlState.isOnMobileDevice
            const petListVisible = drawerState.petList
            const padding = !isMobile && petListVisible
                ? { left: 380, top: 20, right: 20, bottom: 20 }
                : undefined
            map.fitBounds(bounds, padding)
            const listener = window.google.maps.event.addListener(map, 'idle', () => {
                let mapZoom = map.getZoom()!
                mapZoom = mapZoom > DEFAULT_ZOOM ? DEFAULT_ZOOM : mapZoom
                map.setZoom(mapZoom)
                setZoom(mapZoom)
                window.google.maps.event.removeListener(listener)
            })
        }
    }, [map, bounds, follow])

    return <>{markers}</>
}
